/**
 * TND-008 §8.1 bis — le refus `409 GED_FILE_OWNED_BY_TENDER`, lu par son CODE.
 *
 * ## Pourquoi un module à part
 *
 * Deux surfaces tombent sur ce refus : le coffre (`ged-files.ts`) et l'onglet
 * « Documents » d'une fiche (`useEntityDocumentsTabState`). Deux phrases rédigées
 * à deux endroits divergeraient au premier changement de contrat — celui du
 * 2026-09-17 a déjà retiré « depuis le coffre », faux dans l'onglet d'un client.
 *
 * ## Pourquoi le CODE et pas le message
 *
 * Mesuré le 2026-09-16 : `deleteFile` et `updateFile` jetaient « Failed to delete
 * file: 409 », et l'onglet client montrait la phrase du serveur mot pour mot,
 * donc en français dans l'interface anglaise. Le serveur envoie
 * `tender_reference` à part : l'écran compose SA phrase, et le message serveur ne
 * sert que de repli quand aucune traduction n'est chargée.
 *
 * ⚠️ Les clés sont écrites EN ENTIER, DANS l'appel à `t` : une clé construite
 * (`…ownedByTenderGeste.${geste}`) échapperait à tout extracteur, et une clé
 * absente des deux catalogues laisse la parité verte. Mesuré le 2026-09-17 :
 * rangées en valeurs d'une table (`{ cle: '…' }`), les quatre clés de geste
 * étaient invisibles au gardien `i18n-keys-exist`, qui ne lit que les appels.
 *
 * Les phrases qui parlent d'un DOSSIER — celui qu'on vise, celui qu'on déplace,
 * celui qu'Argalis gère, celui qu'on crée — vivent dans
 * `ged-owned-by-tender-folder` ; les lecteurs d'erreur des portes DOSSIER, leur
 * moule et le 410 d'une consultation anonymisée, dans `ged-owned-by-tender-erreurs`.
 * Les deux sont ré-exportés ici.
 */

import i18next, { type TFunction } from 'i18next'

import type { ApiError } from '@/lib/api-error'

import { GED_FILE_LEGAL_RETENTION, phraseDeConservationLegale } from './ged-conservation-legale'
import {
    TENDER_ANONYMIZED,
    erreurDeCreationDunDossier,
    erreurDunDossierDeplace,
    erreurDunDossierDuCoffre,
    erreurLue,
    phraseDUneConsultationAnonymisee,
} from './ged-owned-by-tender-erreurs'
import {
    GED_FILE_OWNED_BY_TENDER,
    GED_FOLDER_OWNED_BY_TENDER,
    PARENT_NOT_FOUND,
    SYSTEM_FOLDER_PROTECTED,
    phraseDeCreationDunDossier,
    phraseDunDossierSysteme,
    phraseDuRefusDUnDossier,
    phraseDuRefusDUnDossierCible,
    phraseDuRefusDUnDossierDeplace,
} from './ged-owned-by-tender-folder'
import {
    GED_METADATA_TENDER_ID_RESERVED,
    phraseDuRattachementReserve,
} from './ged-rattachement-reserve'

export {
    GED_FILE_LEGAL_RETENTION,
    GED_FILE_OWNED_BY_TENDER,
    GED_FOLDER_OWNED_BY_TENDER,
    GED_METADATA_TENDER_ID_RESERVED,
    PARENT_NOT_FOUND,
    SYSTEM_FOLDER_PROTECTED,
    TENDER_ANONYMIZED,
    erreurDeCreationDunDossier,
    erreurDunDossierDeplace,
    erreurDunDossierDuCoffre,
    phraseDUneConsultationAnonymisee,
    phraseDeConservationLegale,
    phraseDeCreationDunDossier,
    phraseDunDossierSysteme,
    phraseDuRattachementReserve,
    phraseDuRefusDUnDossier,
    phraseDuRefusDUnDossierCible,
    phraseDuRefusDUnDossierDeplace,
}

/** Le geste que l'écran a TENTÉ. Miroir de `GESTES` (`_deletion_guards_portee.py`) :
 *  le serveur ne renvoie pas le geste à part, seulement dans sa phrase.
 *
 *  ⚠️ **`archive` ne passe PAS par la clé générique**, et ce n'est pas un oubli :
 *  « on ne peut pas le {{geste}} » rendrait « on ne peut pas LE archiver ».
 *  L'élision est dans la phrase, donc la phrase est écrite en entier —
 *  `ownedByTenderArchive` / `…ArchiveNoReference`, dans les deux catalogues.
 *
 *  ⚠️ `edit` est parti le 2026-09-18 avec la garde qui l'émettait (D1) : garder
 *  la branche posait un chemin que le serveur ne peut plus emprunter. Et aucune
 *  surface n'offre encore « Archiver » (`useEntityDocuments.archive` a zéro
 *  appelant) — mais les quatre services le REFUSENT : la phrase est câblée avant
 *  le bouton, pour que le lot C12 n'ait pas à la rédiger sous un rouge.
 */
export type GesteRefuse = 'delete' | 'move' | 'replace' | 'archive'

function verbe(t: TFunction, geste: Exclude<GesteRefuse, 'archive'>): unknown {
    switch (geste) {
        case 'delete':
            return t('sales.tenders.documents.ownedByTenderGeste.delete', { defaultValue: 'supprimer' })
        case 'move':
            return t('sales.tenders.documents.ownedByTenderGeste.move', { defaultValue: 'déplacer' })
        case 'replace':
            return t('sales.tenders.documents.ownedByTenderGeste.replace', { defaultValue: 'remplacer' })
    }
}

/** `t` rend `undefined` tant qu'i18next n'est pas initialisé (mesuré sur la
 *  23.16.8) : une chaîne vide ou absente ne doit jamais devenir le toast. */
function texte(valeur: unknown): string | null {
    return typeof valeur === 'string' && valeur.trim() ? valeur : null
}

/** La phrase d'un ARCHIVAGE refusé : écrite en entier, élision comprise. */
function phraseDArchivage(t: TFunction, reference: unknown): string | null {
    if (typeof reference === 'string' && reference.trim()) {
        return texte(
            t('sales.tenders.documents.ownedByTenderArchive', {
                defaultValue:
                    "Ce document fait partie du dossier de l'appel d'offres {{reference}} : on ne peut pas l'archiver ici. Passez par la fiche de l'appel d'offres, onglet Documents.",
                reference,
            }),
        )
    }
    return texte(
        t('sales.tenders.documents.ownedByTenderArchiveNoReference', {
            defaultValue:
                "Ce document fait partie du dossier d'un appel d'offres : on ne peut pas l'archiver ici. Passez par la fiche de l'appel d'offres, onglet Documents.",
        }),
    )
}

/** La phrase du contrat, dans la langue de l'écran. `null` si rien n'est traduit. */
export function phraseDePieceDAppelDOffres(
    t: TFunction,
    geste: GesteRefuse,
    reference: unknown,
): string | null {
    if (geste === 'archive') return phraseDArchivage(t, reference)
    const leVerbe = texte(verbe(t, geste))
    if (!leVerbe) return null
    // Sans référence, « de l'appel d'offres  : » serait une phrase trouée : la
    // variante dit « d'un appel d'offres », comme le serveur.
    if (typeof reference === 'string' && reference.trim()) {
        return texte(
            t('sales.tenders.documents.ownedByTender', {
                defaultValue:
                    "Ce document fait partie du dossier de l'appel d'offres {{reference}} : on ne peut pas le {{geste}} ici. Passez par la fiche de l'appel d'offres, onglet Documents.",
                reference,
                geste: leVerbe,
            }),
        )
    }
    return texte(
        t('sales.tenders.documents.ownedByTenderNoReference', {
            defaultValue:
                "Ce document fait partie du dossier d'un appel d'offres : on ne peut pas le {{geste}} ici. Passez par la fiche de l'appel d'offres, onglet Documents.",
            geste: leVerbe,
        }),
    )
}

/**
 * La phrase d'une erreur déjà levée, ou `null` si ce n'est pas un de ces refus.
 *
 * Lit `code` et `details.tender_reference` à la forme d'`ApiError`. Les DEUX
 * codes y passent : le PATCH d'un rangement peut tomber sur la pièce POSSÉDÉE
 * (409 `GED_FILE_…`) comme sur une DESTINATION possédée (409 `GED_FOLDER_…`, la
 * pièce étant alors libre) — une porte, deux refus, et l'appelant n'a pas à
 * savoir lequel des deux il va recevoir.
 *
 * ✅ 2026-09-18 — `useEntityDocuments.readError` lève désormais une `ApiError`
 * complète : il gardait `code` et jetait `details`, et la phrase de l'onglet
 * sortait sans la consultation (« a tender file »).
 *
 * ✅ **B4 (2026-09-19) — `GED_FILE_LEGAL_RETENTION` y passe aussi** : même
 * fonction serveur (`refuser_une_piece_dappel_doffres` l.157) quand la garde de
 * la consultation se RETIRE, et il rendait `null` ici. Lire les codes ICI plutôt
 * que dans chaque appelant est ce qui ferme les DEUX portes d'un geste :
 * `erreurDuCoffre` et `refusDe` passent tous les deux par cette fonction.
 */
export function phraseDuRefusDAppelDOffres(
    t: TFunction,
    erreur: unknown,
    geste: GesteRefuse,
): string | null {
    if (!erreur || typeof erreur !== 'object') return null
    const { code, details } = erreur as { code?: unknown; details?: { tender_reference?: unknown } }
    if (code === GED_FOLDER_OWNED_BY_TENDER) return phraseDuRefusDUnDossierCible(t, erreur)
    if (code === GED_FILE_LEGAL_RETENTION) return phraseDeConservationLegale(t)
    // ✅ E6 (2026-09-19) puis F7 (le même soir) : DEUX 422 de plus, tous deux
    // écrits en français en dur côté serveur et sans AUCUN lecteur à l'écran —
    // `grep` rendait 0 occupation pour le rattachement réservé, et 2 occurrences
    // toutes deux en commentaire pour le dossier système. Ils se lisent ici et
    // non chez l'appelant, pour la même raison que les trois premiers : une
    // porte, plusieurs refus, et l'appelant n'a pas à savoir lequel il reçoit.
    if (code === GED_METADATA_TENDER_ID_RESERVED) return phraseDuRattachementReserve(t)
    if (code === SYSTEM_FOLDER_PROTECTED) return phraseDunDossierSysteme(t, erreur)
    // 🔴 2026-09-23 : le 410 d'une consultation anonymisée — la pièce, ou sa destination.
    if (code === TENDER_ANONYMIZED) return phraseDUneConsultationAnonymisee(t, erreur)
    if (code !== GED_FILE_OWNED_BY_TENDER) return null
    return phraseDePieceDAppelDOffres(t, geste, details?.tender_reference)
}

/**
 * L'erreur d'une porte du coffre qui a refusé : une `ApiError` complète.
 *
 * Elle remplace `new Error('Failed to … : 409')`, qui jetait le code, la
 * phrase ET le `request_id` — `toastApiError` ne pouvait alors ni reconnaître
 * un refus métier ni proposer le code support d'une vraie panne.
 *
 * ✅ **E1 / F6 : elle sert aussi les portes de MASSE et la COPIE** — les deux
 * gestes de masse, leurs deux aperçus (`ged-bulk-apercu.ts`) et `copyFile`, qui
 * jetaient tous le même statut HTTP. Le `geste` n'y est qu'un repli : le refus
 * qui y arrive est celui de la DESTINATION, dont la phrase n'a pas de verbe. Le
 * dépôt XHR, lui, n'a pas de `Response` : même moule, dans `ged-upload`.
 */
export async function erreurDuCoffre(
    res: Response,
    geste: GesteRefuse,
    repli: string,
): Promise<ApiError> {
    return erreurLue(res, repli, (e) => phraseDuRefusDAppelDOffres(i18next.t, e, geste))
}
